import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { QUERY_KEYS } from "@/services/api/queryKeys";
import { MUTATION_KEYS } from "@/services/api/mutationKeys";
import type {
  PortalAdmin,
  PortalAdminResponse,
  PortalListItem,
  PortalsResponse,
  CreatePortalAdminPayload,
  UpdatePortalAdminPayload,
} from "@/types";

type PortalAdminApi = Omit<PortalAdmin, "portal_ids"> & {
  portal_ids?: string[];
  portal_id?: string | null;
  portal_assignments?: { portal_id: string; portal_slug: string }[];
  permissions?: {
    portals?: string[];
  };
};

export const useUsers = () => {
  const queryClient = useQueryClient();

  const adminsQuery = useQuery({
    queryKey: QUERY_KEYS.PORTAL_ADMINS.LIST(),
    queryFn: async () => {
      const response = await apiClient.get<PortalAdminApi[]>(
        API_ENDPOINTS.SYSTEM.PORTAL_ADMINS.BASE
      );
      const admins: PortalAdminApi[] = Array.isArray(response.data)
        ? response.data
        : [];
      return admins.map((admin) => {
        const portalIds =
          admin.portal_ids ??
          (admin.portal_assignments
            ? admin.portal_assignments.map((a) => a.portal_id)
            : admin.portal_id
              ? [admin.portal_id]
              : []);
        return {
          ...admin,
          portal_ids: portalIds,
          portal_assignments: admin.portal_assignments ?? [],
        } as PortalAdmin;
      });
    },
  });

  const portalsQuery = useQuery({
    queryKey: QUERY_KEYS.PORTALS.LIST(),
    queryFn: async () => {
      const response = await apiClient.get<PortalsResponse>(
        API_ENDPOINTS.SYSTEM.PORTALS.BASE
      );
      return response.data.portals;
    },
  });

  const createAdminMutation = useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_PORTAL_ADMIN],
    mutationFn: async (payload: CreatePortalAdminPayload) => {
      const response = await apiClient.post<PortalAdminResponse>(
        API_ENDPOINTS.SYSTEM.PORTAL_ADMINS.BASE,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PORTAL_ADMINS.ALL,
      });
    },
  });

  const updateAdminMutation = useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_PORTAL_ADMIN],
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdatePortalAdminPayload;
    }) => {
      const response = await apiClient.patch<PortalAdminResponse>(
        API_ENDPOINTS.SYSTEM.PORTAL_ADMINS.DETAIL(id),
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PORTAL_ADMINS.ALL,
      });
    },
  });

  const deleteAdminMutation = useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_PORTAL_ADMIN],
    mutationFn: async (id: string) => {
      await apiClient.delete(API_ENDPOINTS.SYSTEM.PORTAL_ADMINS.DETAIL(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PORTAL_ADMINS.ALL,
      });
    },
  });

  return {
    admins: adminsQuery.data ?? [],
    isLoading: adminsQuery.isLoading,
    error: adminsQuery.error,
    portals: portalsQuery.data ?? ([] as PortalListItem[]),
    portalsLoading: portalsQuery.isLoading,
    createAdmin: createAdminMutation.mutateAsync,
    isCreating: createAdminMutation.isPending,
    updateAdmin: updateAdminMutation.mutateAsync,
    isUpdating: updateAdminMutation.isPending,
    deleteAdmin: deleteAdminMutation.mutate,
    deleteAdminAsync: deleteAdminMutation.mutateAsync,
    isDeleting: deleteAdminMutation.isPending,
  };
};
